import React, { useState, useEffect } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import LoadingSpinner from "./LoadingSpinner";
import { LocationStat } from "../interfaces/StatsType";

interface LocationsMapProps {
  locations: LocationStat[]; // z.B. [{ location: "Esslingen am Neckar", totalFlights: 12 }, ...]
}

interface MarkerData {
  location: string;
  totalFlights: number;
  lat: number;
  lng: number;
}

const LocationsMap: React.FC<LocationsMapProps> = ({ locations }) => {
  const [markers, setMarkers] = useState<MarkerData[]>([]);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY_LOCATION || "",
  });

  const enrichAddress = (address: string) => {
    const lower = address.toLowerCase();
    if (!lower.includes("esslingen") && !lower.includes("deutschland")) {
      return `${address}Baden-Württemberg, Deutschland`;
    }
    return address;
  };


  useEffect(() => {
    if (!isLoaded || locations.length === 0) return;
    const geocoder = new window.google.maps.Geocoder();

    const esslingenCenter = { lat: 48.739, lng: 9.298 };
    const esslingenBounds = new window.google.maps.LatLngBounds(
    new window.google.maps.LatLng(esslingenCenter.lat - 0.5, esslingenCenter.lng - 0.5),
    new window.google.maps.LatLng(esslingenCenter.lat + 0.5, esslingenCenter.lng + 0.5)
  );

    const geocodeAddress = (address: string): Promise<{ lat: number; lng: number }> => {
        const query = enrichAddress(address);
        return new Promise((resolve, reject) => {
        geocoder.geocode(
            {
              address: query,
              bounds: esslingenBounds,
              componentRestrictions: { country: "de" },
              region: "de"
            },
            (results, status) => {
            if (status === "OK" && results && results.length > 0) {
              const loc = results[0].geometry.location;
              console.log(`Geocoded ${address}:`, { lat: loc.lat(), lng: loc.lng() });
              resolve({ lat: loc.lat(), lng: loc.lng() });
            } else {
              console.error(`Geocoding für "${address}" fehlgeschlagen:`, status);
              reject(status);
            }
          });
          
      });
    };

    Promise.all(
      locations.map((locStat) =>
        geocodeAddress(locStat.location)
          .then((coords) => ({
            location: locStat.location,
            totalFlights: locStat.totalFlights,
            lat: coords.lat,
            lng: coords.lng,
          }))
          .catch((error) => {
            console.error("Geocoding error for", locStat.location, error);
            return null;
          })
      )
    ).then((results) => {
      const validMarkers = results.filter((m) => m !== null) as MarkerData[];
      setMarkers(validMarkers);
    });
  }, [isLoaded, locations]);

  const center =
    markers.length > 0
      ? { lat: markers[0].lat, lng: markers[0].lng }
      : { lat: 0, lng: 0 };

  if (loadError) {
    return <div>Fehler beim Laden der Google Maps API</div>;
  }

  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: "400px" }}
      center={center}
      zoom={6}
    >
      {markers.map((marker, index) => (
        <Marker
          key={index}
          position={{ lat: marker.lat, lng: marker.lng }}
          title={`${marker.location} (${marker.totalFlights} Flüge)`}
        />
      ))}
    </GoogleMap>
  );
};

export default LocationsMap;
